import { useState, useCallback } from "react";
import { FilterOptions } from "../types/order";
import { useDebounce } from "./useDebounce";

const DEFAULT_FILTERS: FilterOptions = {
  timeRange: "Today",
  customStartDate: "",
  customEndDate: "",
  selectedStaff: "",
};

export const useOrderFilters = (initialSearch = "") => {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getFilterPlaceholder = useCallback(() => {
    if (!isFilterApplied) return "Search by name, phone number or order ID";

    const parts: string[] = [];
    if (appliedFilters.timeRange && appliedFilters.timeRange !== "Today") {
      parts.push(`Time: ${appliedFilters.timeRange}`);
    }
    if (appliedFilters.timeRange === "CustomizeDate" && appliedFilters.customStartDate && appliedFilters.customEndDate) {
      const start = new Date(appliedFilters.customStartDate).toLocaleDateString();
      const end = new Date(appliedFilters.customEndDate).toLocaleDateString();
      parts.push(`Date: ${start} to ${end}`);
    }
    if (appliedFilters.selectedStaff) {
      parts.push(`Staff: ${appliedFilters.selectedStaff}`);
    }

    return parts.join(" | ") || "Search by name, phone number or order ID";
  }, [appliedFilters, isFilterApplied]);

  const applyFilters = useCallback(() => {
    setIsFilterApplied(true);
    setAppliedFilters(filters);
  }, [filters]);

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setIsFilterApplied(false);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    appliedFilters,
    isFilterApplied,
    debouncedSearchTerm,
    getFilterPlaceholder,
    applyFilters,
    resetFilters,
  };
};
